import _ from 'lodash'
import { Dispatch, SetStateAction, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { skipToken } from '@reduxjs/toolkit/query'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { useGetAdminEstablishmentsQuery, useGetAdminStaffsQuery } from '@/core/services/admin'
import { useGetWaitingRoomQuery, useUpdateWaitingRoomMutation } from '@/core/services/waiting-room'
import { WaitingRoomForm } from '@/features/waiting-room'
import { getApiErrorMessage } from '@/utils/errors'

export default function UpdateWaitingRoomForm({ editable, setEditable }: { editable: boolean; setEditable: Dispatch<SetStateAction<boolean>> }) {
  const { waitingRoomId } = useParams<'waitingRoomId'>()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const { doctors } = useGetAdminStaffsQuery({ filter: { status: 'MEDECIN' } }, { selectFromResult: ({ data }) => ({ doctors: data?.[0] ?? [] }) })
  const { data: establishments } = useGetAdminEstablishmentsQuery()
  const { data: waitingRoom, isLoading: isLoadingWaitingRoom } = useGetWaitingRoomQuery(waitingRoomId ?? skipToken)
  const [updateWaitingRoom, { isLoading: isUpdating }] = useUpdateWaitingRoomMutation()

  const doctorsData = useMemo(
    () =>
      _.map(
        doctors.filter((doctor) => (editable ? true : waitingRoom?.doctors.includes(doctor._id))),
        (doctor) => ({
          id: doctor._id,
          cps: doctor.cps || '',
          name: doctor.userInfo.name,
          surname: doctor.userInfo.surname,
          email: doctor.email,
          stripeActivated: doctor.stripeActivated || false,
        })
      ),
    [editable, doctors, waitingRoom]
  )

  const establishmentsData = useMemo(
    () =>
      _.map(editable ? establishments : waitingRoom?.establishments, (establishment) => ({
        id: establishment._id,
        active: establishment.active || false,
        name: establishment.name,
        admin: establishment.admin ? `${establishment.admin.userInfo.name} ${establishment.admin.userInfo.surname}` : '',
      })),
    [editable, establishments, waitingRoom]
  )

  const initialValues = useMemo(
    () => ({
      avgWaitingTime: waitingRoom?.avgWaitingTime || 15,
      allowPatientSelection: waitingRoom?.allowPatientSelection ?? false,
      description: waitingRoom?.description || '',
      doctors: waitingRoom?.doctors ?? [],
      establishments: _.map(waitingRoom?.establishments, '_id'),
      isPrioritized: waitingRoom?.isPrioritized || false,
      name: waitingRoom?.name || '',
      openingHours:
        waitingRoom?.openingHours ||
        _.range(7).map((day) => ({
          day,
          periods: [],
        })),
      specialities: waitingRoom?.specialities || [],
      price: waitingRoom?.price || 0,
      paymentReceiverId: waitingRoom?.paymentReceiverId || '',
      paymentDialogConfig: waitingRoom?.paymentDialogConfig ?? { enabled: false, showForRoles: [] },
      fairDistribution: {
        enabled: waitingRoom?.fairDistribution?.enabled ?? false,
        maxConcurrent: waitingRoom?.fairDistribution?.maxConcurrent ?? 1,
        cooldownSeconds: waitingRoom?.fairDistribution?.cooldownSeconds ?? 60,
        // Legacy rooms predate these fields; default so the form (and its zod schema) stay valid.
        strategy: waitingRoom?.fairDistribution?.strategy ?? ('cap' as const),
        offerTtlSeconds: waitingRoom?.fairDistribution?.offerTtlSeconds ?? 30,
        prominentOffer: waitingRoom?.fairDistribution?.prominentOffer ?? false,
      },
      readinessConfig: {
        enabled: waitingRoom?.readinessConfig?.enabled ?? false,
        prioritizeReady: waitingRoom?.readinessConfig?.prioritizeReady ?? false,
        checklistTemplateId: waitingRoom?.readinessConfig?.checklistTemplateId ?? '',
      },
      nurseDoctorExclusions: waitingRoom?.nurseDoctorExclusions ?? { enabled: false, rules: [] },
      coPresenceConfig: {
        enabled: waitingRoom?.coPresenceConfig?.enabled ?? false,
        allowAssign: waitingRoom?.coPresenceConfig?.allowAssign ?? false,
      },
    }),
    [waitingRoom]
  )

  return (
    <WaitingRoomForm
      doctors={doctorsData}
      editable={editable}
      establishments={establishmentsData}
      initialValues={initialValues}
      isAdmin
      isSubmitting={isUpdating}
      onSubmit={async (values) => {
        if (!waitingRoomId) return
        if (values.paymentReceiverId === '') values.paymentReceiverId = undefined
        if (values.readinessConfig.checklistTemplateId === '') values.readinessConfig.checklistTemplateId = undefined
        try {
          await updateWaitingRoom({
            id: waitingRoomId,
            updates: {
              ...values,
              specialities: _.map(values.specialities, (speciality) => _.pick(speciality, ['code', 'name', 'selectByDefault'])),
            },
          }).unwrap()
          enqueueSnackbar(t('notification.waitingRoom.update.success'), { variant: 'success' })
          setEditable(false)
        } catch (error) {
          enqueueSnackbar(getApiErrorMessage(error, t('notification.waitingRoom.update.error')), { variant: 'error' })
        }
      }}
    />
  )
}
